import { FormElement, Form, Field, FormClassComponent } from '@progress/kendo-react-form'
import DefaultButton from 'components/Buttons/DefaultButton/DefaultButton'
import SaveButton from 'components/Buttons/SaveButton/SaveButton'
import DealerSelect from 'components/Dealers/DealerSelect'
import FormDatePicker from 'components/Form/FormDatePicker'
import FormInput from 'components/Form/FormInput'
import FormTextArea from 'components/Form/FormTextArea'
import { DEFAULT_PERMISSION_MESSAGE } from 'core/consts'
import { focusFirstError, numberToCurrency } from 'core/helpers'
import { V1ApiCasesClient, V1ApiDealersClient, V1ApiUsersClient } from 'core/http-common'
import { RefObject, useEffect, useRef, useState } from 'react'
import { Button, Col, Modal, Row } from 'react-bootstrap'
import { useDispatch } from 'react-redux'
import { DcsPermissions, ICustomColumn, IDealerChangedEvent, IDropDownValue, TypeEnum } from 'types'
import toast from 'core/toast'
import { DeletedStateEnum, DealerContactDto, ProductTypeEnum, DealerBillingInformationDto, DealerInvoiceTotalsDto } from '@v1DealersApi'
import { EmailValidator, PhoneValidator, PhoneValidatorNotRequired, RequiredValidator } from 'core/Validators'
import { AppDispatch } from 'state/store'
import { startLoadingAction, endLoadingAction } from 'state/actions/appActions'
import { CaseDto, CreateCaseDomainDto, UpdateCaseDomainDto, UpdateCaseDto } from '@v1CasesApi'
import FormTypeComboBox from 'components/Form/FormTypeComboBox'
import { hasPermission } from 'state/reduxHelpers'
import CasesGrid from 'components/Cases/CasesGrid'
import { ITypeComboBoxRef } from 'components/ComboBox/TypeComboBox'
import moment from 'moment'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
interface IProps {
    editable: boolean,
    caseToEdit: CaseDto | undefined,
    productType: ProductTypeEnum,
    caseFormRef: RefObject<FormClassComponent>,
    onCaseUpdated: () => Promise<void>
}

const CaseInformationForm = ({ editable, caseToEdit, productType, caseFormRef, onCaseUpdated }: IProps) => {

    const formRef = caseFormRef

    const dealerSelectFieldRef = useRef<ITypeComboBoxRef>(null)

    const dispatch = useDispatch<AppDispatch>()
    const [showDealerContactModal, setShowDealerContactModal] = useState<boolean>(false)
    const [selectedDealerId, setSelectedDealerId] = useState<number>()

    const [loaded, setLoaded] = useState(false)

    const [statusId, setStatusId] = useState<number | undefined | null>(caseToEdit?.caseStatusId)
    const [reasonId, setReasonId] = useState<number | undefined | null>(caseToEdit?.caseReasonId)
    const [approveId, setApproveId] = useState<number | undefined | null>(caseToEdit?.approvedById)

    // data cards
    const [monthlyRecurringCost, setMonthlyRecurringCost] = useState<number>()
    const [fetchDeletedDealer, setFetchDeletedDealer] = useState<boolean>()
    const [contractStatus, setContractStatus] = useState<string>()
    const [accountOwner, setAccountOwner] = useState<string>()
    const [billingInformation, setBillingInformation] = useState<DealerBillingInformationDto>()
    const [openInvoiceTotals, setOpenInvoiceTotals] = useState<DealerInvoiceTotalsDto>()
    const [paymentMethod, setPaymentMethod] = useState<string>('')

    // caseDomain
    const [previousNameServer, setPreviousNameServer] = useState<string>()
    const [previousNameServer2, setPreviousNameServer2] = useState<string>()
    const [previousNameServer3, setPreviousNameServer3] = useState<string>()
    const [previousMx, setPreviousMx] = useState<string>()
    const [previousMx2, setPreviousMx2] = useState<string>()
    const [previousArecord, setPreviousArecord] = useState<string>()
    const [previousWhoIs, setPreviousWhoIs] = useState<string>()

    const caseColumns: ICustomColumn[] = [
        { field: 'age', title: 'Age', filterType: 'date', className: 'case-age-cell', headerClassName: 'case-age-header' },
        { field: 'lastModifiedDate', title: 'Last Modified', format: '{0:g}', filterType: 'date' },
        { field: 'caseId', title: 'Case', sortable: true, filterType: 'numeric' },
        { field: 'assignedToUser', title: 'Owner', filterType: 'text' },
        { field: 'subject', title: 'Subject', filterType: 'text', className: 'case-subject-cell', headerClassName: 'case-subject-header' },
        { field: 'caseStatus', title: 'Status', filterType: 'text' },
    ]

    const handleDealerClear = async () => {
        handleDealerIdChange(undefined)
    }

    const handleDealerContactModal = () => {

        if (!hasPermission(DcsPermissions.AddDealerContact)) {
            toast.error(DEFAULT_PERMISSION_MESSAGE)
        } else {
            setShowDealerContactModal(!showDealerContactModal)
        }
    }

    // handles refreshing of f dealer contact dropdown when a new contact is created
    const [dealerContactKey, setDealerContactKey] = useState<number>(0)
    const [newContact, setNewContact] = useState<DealerContactDto>()

    const handleAddDealerContactSubmit = async (values: DealerContactDto) => {
        try {
            await dispatch(startLoadingAction())

            if (selectedDealerId) {
                values.dealerId = selectedDealerId
            }

            var result = await V1ApiDealersClient.createContact(values)
            if (result.success) {
                toast.success('Dealer Contact was successfully created.')
                setShowDealerContactModal(false)

                setNewContact(result.payload)
                dealerSelectFieldRef.current?.fetchData()

            } else {
                toast.error(result.errors[0])
            }

        } catch (e) {
            toast.responseErrors(e)
        }
        finally {
            await dispatch(endLoadingAction())

        }
    }

    useEffect(() => {

        if (newContact && caseToEdit) {
            caseToEdit.dealerContactId = newContact.dealerContactId
        }

        formRef.current?.valueSetter('dealerContactId', newContact?.dealerContactId)
        setDealerContactKey(prev => prev + 1)
    }, [newContact])

    const handleSubmit = async (values: UpdateCaseDto) => {
        try {
            await dispatch(startLoadingAction())

            values.dealerId = selectedDealerId

            // check for case domain
            if (values.caseReasonId == 10) {

                if (caseToEdit?.caseDomain) {

                    try {

                        V1ApiCasesClient.updateCaseDomain(caseToEdit.caseDomain?.caseDomainId, new UpdateCaseDomainDto({
                            previousNameServer: previousNameServer,
                            previousNameServer2: previousNameServer2,
                            previousNameServer3: previousNameServer3,
                            previousMx: previousMx,
                            previousMx2: previousMx2,
                            previousArecord: previousArecord,
                            previousWhoIs: previousWhoIs
                        }))
                    } catch (e) {

                        toast.responseErrors(e)
                    }
                } else {

                    try {

                        V1ApiCasesClient.createCaseDomain(new CreateCaseDomainDto({
                            caseId: values.caseId,
                            previousNameServer: previousNameServer,
                            previousNameServer2: previousNameServer2,
                            previousNameServer3: previousNameServer3,
                            previousMx: previousMx,
                            previousMx2: previousMx2,
                            previousArecord: previousArecord,
                            previousWhoIs: previousWhoIs
                        }))
                    } catch (e) {

                        toast.responseErrors(e)
                    }
                }
            }

            var result = await V1ApiCasesClient.updateCase(values.caseId.toString(), values)

            if (result.success) {
                toast.success('Case was successfully updated.')

                await onCaseUpdated()
            }

        } catch (e) {
            toast.responseErrors(e)
        }
        finally {
            await dispatch(endLoadingAction())
        }
    }

    const handleDealerIdChange = async (event: IDealerChangedEvent | undefined) => {
        setSelectedDealerId(event?.dealerId)
    }

    const handleDealerChange = async (e: IDealerChangedEvent) => {

        formRef.current?.valueSetter('dealerContactSelection', undefined)
        formRef.current?.valueSetter('dealerContactId', undefined)
        formRef.current?.valueSetter('dealerContact', undefined)

        if (selectedDealerId != e.dealerId)
            handleDealerIdChange(e)
    }

    const fetch = async () => {

        // we need to account for a case not having a dealerId, leaving blank the fields and removing functionality on the page that relies on it.
        try {
            if (caseToEdit?.dealerId) {
                const [
                    activeCaseUsers,
                    products,
                    dealer,
                    owner,
                    invoice,
                    billing,
                ] = await Promise.all([
                    V1ApiUsersClient.getUsers(DeletedStateEnum.Active),
                    V1ApiDealersClient.getProducts(caseToEdit?.dealerId, productType, '', 'createdDate', true, undefined, undefined, false, true),
                    V1ApiDealersClient.getDealer(caseToEdit?.dealerId),
                    V1ApiDealersClient.getDealerOwner(caseToEdit?.dealerId),
                    V1ApiDealersClient.getOpenInvoiceTotals(caseToEdit.dealerId),
                    V1ApiDealersClient.getBillingInformation(caseToEdit.dealerId),
                ])

                setBillingInformation(billing)
                setOpenInvoiceTotals(invoice)

                const paymentType = billingInformation?.invoiceTypeId
                if (paymentType) {
                    if (paymentType === 1) {
                        setPaymentMethod('Credit Card')
                    } else if (paymentType === 2) {
                        setPaymentMethod('ACH')
                    } else if (paymentType === 3) {
                        setPaymentMethod('Mail')
                    }
                }

                setFetchDeletedDealer(dealer.deleted)

                // contract status
                if (dealer.contractEndDate) {
                    const contractDate = moment(dealer.contractEndDate)
                    if (contractDate.isAfter(moment())) {
                        setContractStatus(`In Contract: ${contractDate.format('MM/DD/YYYY')}`)
                    } else {
                        setContractStatus(`Expired: ${contractDate.format('MM/DD/YYYY')}`)
                    }
                } else {
                    setContractStatus('No Contract Date')
                }

                setAccountOwner(owner.ownerName)
                setMonthlyRecurringCost(products.totalProductCost?.totalProductCost)

            }
            setLoaded(true)
        }
        catch (e) {
            toast.responseErrors(e)
        }
    }

    useEffect(() => {
        if (caseToEdit) {
            fetch()

            if (caseToEdit.caseDomain) {
                setPreviousNameServer(caseToEdit.caseDomain?.previousNameServer)
                setPreviousNameServer2(caseToEdit.caseDomain?.previousNameServer2)
                setPreviousNameServer3(caseToEdit.caseDomain?.previousNameServer3)
                setPreviousMx(caseToEdit.caseDomain?.previousMx)
                setPreviousMx2(caseToEdit.caseDomain?.previousMx2)
                setPreviousArecord(caseToEdit.caseDomain?.previousArecord)
                setPreviousWhoIs(caseToEdit.caseDomain?.previousWhoIs)
            }
        }

    }, [caseToEdit, selectedDealerId])

    useEffect(() => {
        if (fetchDeletedDealer !== undefined) {
            setReasonId(caseToEdit?.caseReasonId)
            setStatusId(caseToEdit?.caseStatusId)
            setSelectedDealerId(caseToEdit?.dealerId)
            setApproveId(caseToEdit?.approvedById)
        }
    }, [caseToEdit, fetchDeletedDealer])

    const approveCase = async () => {
        try {
            if (caseToEdit) {
                await dispatch(startLoadingAction())

                var result = await V1ApiCasesClient.approveCase(caseToEdit?.caseId)
                if (result.success) {
                    toast.success('Case Approved.')
                    setApproveId(result.payload?.approvedById)
                    formRef.current?.valueSetter('approvedDate', result.payload?.approvedDate)
                } else {
                    toast.error(result.errors[0])
                }
            }

        } catch (e) {
            toast.responseErrors(e)
        }
        finally {
            await dispatch(endLoadingAction())

        }
    }

    const handleScroll = () => {
        const element = document.getElementById('OpenCasesSection')
        element?.scrollIntoView({ behavior: 'smooth' })
    }


    return (
        <>
            <div id='CaseInformaitonForm'>
                <Form
                    ref={formRef}
                    onSubmit={(e) => handleSubmit(e as UpdateCaseDto)}
                    initialValues={caseToEdit ? caseToEdit : {}}
                    key={caseToEdit?.caseId}
                    render={(formRenderProps) => (
                        <FormElement>
                            <div className='bg-light shadow-sm p-3 border pb-0'>
                                <Row>
                                    <Col md={5}><h4>Case Information</h4></Col>
                                    <Col md={7} className='text-start text-md-end' >
                                        <DefaultButton variant='default' onClick={handleScroll} className='text-nowrap btn-sm'><FontAwesomeIcon icon={['fas', 'search']} />{' '}View Open Cases</DefaultButton>
                                    </Col>
                                </Row>
                                <hr />
                                <Row className='mb-3'>
                                    <Col md={4}>
                                        <div className='bg-white shadow-sm rounded border mb-2 w-100 p-2'>
                                            <Row>
                                                <Col><b>Contract Status:</b></Col>
                                                <Col className='text-end col-auto'>{contractStatus}</Col>
                                            </Row>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className='bg-white shadow-sm rounded border mb-2 w-100 p-2'>
                                            <Row>
                                                <Col><b>Account Owner:</b></Col>
                                                <Col className='text-end col-auto'>{accountOwner}</Col>
                                            </Row>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className='bg-white shadow-sm rounded border mb-2 w-100 p-2'>
                                            <Row>
                                                <Col><b>Monthly Recurring Revenue:</b></Col>
                                                <Col className='text-end col-auto'>{numberToCurrency(monthlyRecurringCost)}</Col>
                                            </Row>
                                        </div>
                                    </Col>

                                    <Col md={4}>
                                        <div className='bg-white shadow-sm rounded border mb-2 w-100 p-2'>
                                            <Row>
                                                <Col><b>Open Invoice Total:</b></Col>
                                                <Col className='text-end col-auto '>{numberToCurrency(openInvoiceTotals?.openInvoiceTotal)}</Col>
                                            </Row>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className='bg-white shadow-sm rounded border mb-2 w-100 p-2'>
                                            <Row>
                                                <Col><b>Past Due Invoice Total:</b></Col>
                                                <Col className='col-auto text-end'>{numberToCurrency(openInvoiceTotals?.pastDueInvoiceTotal)}</Col>
                                            </Row>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className='bg-white shadow-sm rounded border mb-2 w-100 p-2'>
                                            <Row>
                                                <Col><b>Billed on:</b></Col>
                                                <Col className='col-auto text-end'>{billingInformation?.billDay} by {paymentMethod}</Col>
                                            </Row>
                                        </div>
                                    </Col>
                                    <Col md={12}>
                                        <Field
                                            label='Subject:'
                                            name='subject'
                                            id='subject'
                                            component={FormInput}
                                        />
                                    </Col>
                                    <Col md={12}>
                                        <Field
                                            label='Description:'
                                            name='description'
                                            id='description'
                                            component={FormTextArea}
                                            rows={6}
                                            autoSize={true}
                                        />
                                    </Col>

                                    <Col md={6}>
                                        <Field
                                            component={FormTypeComboBox}
                                            id='firstNameSelection'
                                            name='firstNameSelection'
                                            label='Assigned To:'
                                            placeholder='Select'
                                            typeEnum={TypeEnum.activeUser}
                                            defaultId={caseToEdit?.assignedToUserId}
                                            formRef={formRef}
                                            formIdName='assignedToUserId'
                                            formTextName='firstName'
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <Field
                                            dataItemKey='id'
                                            textField='text'
                                            name='dealerId'
                                            id='dealerId'
                                            label='Dealer:'
                                            required
                                            component={DealerSelect}
                                            dealerId={caseToEdit?.dealerId ? Number(caseToEdit?.dealerId) : undefined}
                                            onDealerCleared={handleDealerClear}
                                            onDealerChanged={handleDealerChange}
                                            withTimeZones={true}
                                            showActiveToggle={true}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <Field
                                            label='Due Date:'
                                            name='dueDate'
                                            id='dueDate'
                                            component={FormDatePicker}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <Field
                                            component={FormTypeComboBox}
                                            name='caseStatusSelection'
                                            id='caseStatusSelection'
                                            label='Status:'
                                            required
                                            validator={RequiredValidator}
                                            placeholder='Select'
                                            typeEnum={TypeEnum.caseStatus}
                                            defaultId={caseToEdit?.caseStatusId}
                                            formRef={formRef}
                                            formIdName='caseStatusId'
                                            formTextName='caseStatus1'
                                            onChange={(e) => {
                                                const value = e.value as IDropDownValue
                                                if (loaded) {
                                                    setStatusId(value && value.id ? value.id : undefined)
                                                    //Clear out substatus
                                                    formRef.current?.valueSetter('caseSubStatus1', undefined)
                                                    formRef.current?.valueSetter('caseSubStatusId', undefined)
                                                    formRef.current?.valueSetter('caseSubStatusSelection', undefined)
                                                }
                                            }}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <Field
                                            component={FormTypeComboBox}
                                            name='caseSubStatusSelection'
                                            id='caseSubStatusSelection'
                                            label='Sub Status:'
                                            disabled={!statusId}
                                            placeholder='Select'
                                            typeParameterId={statusId ? statusId : undefined}
                                            typeEnum={TypeEnum.caseSubStatus}
                                            defaultId={caseToEdit?.caseSubStatusId}
                                            formRef={formRef}
                                            formIdName='caseSubStatusId'
                                            formTextName='caseSubStatus1'
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <Field
                                            component={FormTypeComboBox}
                                            name='caseTypeSelection'
                                            id='caseTypeSelection'
                                            label='Type:'
                                            required
                                            validator={RequiredValidator}
                                            placeholder='Select'
                                            typeEnum={TypeEnum.caseType}
                                            defaultId={caseToEdit?.caseTypeId}
                                            formRef={formRef}
                                            formIdName='caseTypeId'
                                            formTextName='caseType1'
                                        />
                                    </Col>

                                    <Col md={6}>
                                        <Field
                                            component={FormTypeComboBox}
                                            id='caseReasonSelection'
                                            name='caseReasonSelection'
                                            label='Reason:'
                                            required
                                            validator={RequiredValidator}
                                            placeholder='Select'
                                            typeEnum={TypeEnum.caseReason}
                                            defaultId={caseToEdit?.caseReasonId}
                                            formRef={formRef}
                                            formIdName='caseReasonId'
                                            formTextName='caseReason1'
                                            onChange={(e) => {
                                                const value = e.value as IDropDownValue
                                                if (loaded) {
                                                    setReasonId(value && value.id ? value.id : undefined)
                                                    //Clear out substatus
                                                    formRef.current?.valueSetter('caseSubReason1', undefined)
                                                    formRef.current?.valueSetter('caseSubReasonId', undefined)
                                                    formRef.current?.valueSetter('caseSubReasonSelection', undefined)
                                                }
                                            }}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <Field
                                            component={FormTypeComboBox}
                                            name='caseSubReasonSelection'
                                            id='caseSubReasonSelection'
                                            label='Sub Reason:'
                                            disabled={!reasonId}
                                            placeholder='Select'
                                            typeParameterId={reasonId ? reasonId : undefined}
                                            typeEnum={TypeEnum.caseSubReason}
                                            defaultId={caseToEdit?.caseSubReasonId}
                                            formRef={formRef}
                                            formIdName='caseSubReasonId'
                                            formTextName='caseSubReason1'
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <Field
                                            component={FormTypeComboBox}
                                            name='caseOriginSelection'
                                            id='caseOriginSelection'
                                            label='Origin:'
                                            required
                                            validator={RequiredValidator}
                                            placeholder='Select'
                                            typeEnum={TypeEnum.caseOrigin}
                                            defaultId={caseToEdit?.caseOriginId}
                                            formRef={formRef}
                                            formIdName='caseOriginId'
                                            formTextName='caseOrigin1'
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <Field
                                            component={FormTypeComboBox}
                                            name='casePrioritySelection'
                                            id='casePrioritySelection'
                                            label='Priority:'
                                            required
                                            validator={RequiredValidator}
                                            placeholder='Select'
                                            typeEnum={TypeEnum.casePriority}
                                            defaultId={caseToEdit?.casePriorityId}
                                            formRef={formRef}
                                            formIdName='casePriorityId'
                                            formTextName='casePriority1'
                                        />
                                    </Col>

                                    <Col md={12}>
                                        <Row>
                                            <Col md={8}>
                                                <Field
                                                    key={dealerContactKey}
                                                    component={FormTypeComboBox}
                                                    name='dealerContactSelection'
                                                    id='dealerContactSelection'
                                                    label='Dealer Contact:'
                                                    disabled={selectedDealerId === undefined}
                                                    validator={RequiredValidator}
                                                    required
                                                    placeholder='Select'
                                                    typeEnum={TypeEnum.dealerContact}
                                                    typeParameterId={selectedDealerId}
                                                    defaultId={caseToEdit?.dealerContactId}
                                                    formRef={formRef}
                                                    formIdName='dealerContactId'
                                                    formTextName='dealerContact'
                                                />
                                            </Col>
                                            <Col md={4} style={{ alignSelf: 'end' }}>
                                                <DefaultButton variant='default' onClick={handleDealerContactModal} disabled={!selectedDealerId} className='text-nowrap w-100'>Add Contact</DefaultButton>
                                            </Col>
                                        </Row>
                                    </Col>

                                    <Col md={4}>
                                        <Field
                                            component={FormTypeComboBox}
                                            name='approverSelection'
                                            id='approverSelection'
                                            label='Approver:'
                                            disabled={true}
                                            placeholder='Select'
                                            typeEnum={TypeEnum.approveCaseUser}
                                            defaultId={approveId}
                                            formRef={formRef}
                                            formIdName='approvedById'
                                            formTextName='approver'
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <Field
                                            label='Approved Date:'
                                            id='approvedDate'
                                            name='approvedDate'
                                            disabled={true}
                                            component={FormDatePicker}
                                        />
                                    </Col>
                                    <Col md={4} style={{ alignSelf: 'end' }}>
                                        <DefaultButton
                                            variant='default'
                                            onClick={approveCase}
                                            disabled={!hasPermission(DcsPermissions.ApproveCase)}
                                            className='text-nowrap w-100'>
                                            Approve Case
                                        </DefaultButton>
                                    </Col>

                                    {/* Case Reason = Domain Name */}
                                    {formRef.current?.valueGetter('caseReasonId') === 10 &&
                                        <>
                                            <Col md={6}>
                                                <Field
                                                    label='Previous Nameserver:'
                                                    id='caseDomain.previousNameServer'
                                                    name='caseDomain.previousNameServer'
                                                    onChange={(e) => setPreviousNameServer(e.target.value)}
                                                    component={FormInput}
                                                />
                                            </Col>
                                            <Col md={6}>
                                                <Field
                                                    label='Previous Nameserver 2:'
                                                    id='caseDomain.previousNameServer2'
                                                    name='caseDomain.previousNameServer2'
                                                    onChange={(e) => setPreviousNameServer2(e.target.value)}
                                                    component={FormInput}
                                                />
                                            </Col>
                                            <Col md={6}>
                                                <Field
                                                    label='Previous Nameserver 3:'
                                                    name='caseDomain.previousNameServer3'
                                                    id='caseDomain.previousNameServer3'
                                                    onChange={(e) => setPreviousNameServer3(e.target.value)}
                                                    component={FormInput}
                                                />
                                            </Col>
                                            <Col md={6}>
                                                <Field
                                                    label='Previous MX:'
                                                    name='caseDomain.previousMx'
                                                    id='caseDomain.previousMx'
                                                    onChange={(e) => setPreviousMx(e.target.value)}
                                                    component={FormInput}
                                                />
                                            </Col>
                                            <Col md={6}>
                                                <Field
                                                    label='Previous MX 2:'
                                                    id='caseDomain.previousMx2'
                                                    name='caseDomain.previousMx2'
                                                    onChange={(e) => setPreviousMx2(e.target.value)}
                                                    component={FormInput}
                                                />
                                            </Col>
                                            <Col md={6}>
                                                <Field
                                                    label='Previous A Record:'
                                                    id='caseDomain.previousArecord'
                                                    name='caseDomain.previousArecord'
                                                    onChange={(e) => setPreviousArecord(e.target.value)}
                                                    component={FormInput}
                                                />
                                            </Col>
                                            <Col md={12}>
                                                <Field
                                                    label='Previous Whois:'
                                                    name='caseDomain.previousWhoIs'
                                                    id='caseDomain.previousWhoIs'
                                                    onChange={(e) => setPreviousWhoIs(e.target.value)}
                                                    component={FormTextArea}
                                                    rows={2}
                                                    autoSize={true}
                                                />
                                            </Col>
                                        </>
                                    }
                                    {/* Case Reason = Cancelation Request */}
                                    {
                                        formRef.current?.valueGetter('caseReasonId') === 4 &&
                                        <>
                                            <Col md={6}>
                                                <Field
                                                    component={FormTypeComboBox}
                                                    id='cancellationReasonSelection'
                                                    name='cancellationReasonSelection'
                                                    label='Cancellation Reason:'
                                                    required={formRef.current?.valueGetter('caseStatusId') === 6}
                                                    validator={formRef.current?.valueGetter('caseStatusId') === 6 ? RequiredValidator : undefined}
                                                    placeholder='Select'
                                                    typeEnum={TypeEnum.cancellationReason}
                                                    defaultId={caseToEdit?.cancellationReasonId}
                                                    formRef={formRef}
                                                    formIdName='cancellationReasonId'
                                                    formTextName='cancellationReason1'
                                                />
                                            </Col>
                                            <Col md={6}>
                                                <Field
                                                    component={FormTypeComboBox}
                                                    name='competitorSelection'
                                                    id='competitorSelection'
                                                    label='Competitor:'
                                                    required={formRef.current?.valueGetter('caseStatusId') === 6}
                                                    validator={formRef.current?.valueGetter('caseStatusId') === 6 ? RequiredValidator : undefined}
                                                    placeholder='Select'
                                                    typeEnum={TypeEnum.competitor}
                                                    defaultId={caseToEdit?.competitorId}
                                                    formRef={formRef}
                                                    formIdName='competitorId'
                                                    formTextName='CompetitorName'
                                                />
                                            </Col>
                                        </>
                                    }

                                </Row>

                                <Row className='my-4'>
                                    <Col xs={12} sm={4}>
                                        <SaveButton
                                            title={!editable ? DEFAULT_PERMISSION_MESSAGE : undefined}
                                            onClick={() => focusFirstError(formRenderProps)}
                                            type='submit' />
                                    </Col>
                                </Row>
                            </div>
                        </FormElement>
                    )
                    }
                />
                <Row className='mt-3' id='OpenCasesSection'>
                    <Col>
                        {(selectedDealerId !== undefined && selectedDealerId > 0) &&
                            <CasesGrid
                                title='Open Cases'
                                showOpenCases={true}
                                showClosedCases={false}
                                dealerId={selectedDealerId}
                                hideCaseActions={true}
                                forDealerEditPage={true}
                                useSimpleSearch={true}
                                customColumns={caseColumns}
                            />
                        }
                    </Col>
                </Row>
            </div>

            <Modal show={showDealerContactModal} onHide={handleDealerContactModal}>
                <Modal.Header closeButton={showDealerContactModal}>
                    <Modal.Title>Add New Dealer Contact</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form
                        onSubmit={(e) => handleAddDealerContactSubmit(e as DealerContactDto)}
                        render={(formRenderProps) => (
                            <FormElement>
                                <Row>
                                    <Col md={12}>
                                        <Field
                                            label='First Name:'
                                            name='firstName'
                                            id='firstName'
                                            component={FormInput}
                                            required
                                            validator={RequiredValidator}
                                        />
                                        <Field
                                            label='Last Name:'
                                            name='lastName'
                                            id='lastName'
                                            component={FormInput}
                                            required
                                            validator={RequiredValidator}
                                        />
                                        <Field
                                            label='Email:'
                                            name='email'
                                            id='email'
                                            component={FormInput}
                                            required
                                            validator={EmailValidator}
                                        />
                                        <Field
                                            label='Primary Phone:'
                                            name='phone'
                                            id='phone'
                                            component={FormInput}
                                            required
                                            validator={PhoneValidator}
                                        />
                                        <Field
                                            label='Secondary Phone:'
                                            id='secondaryPhone'
                                            name='secondaryPhone'
                                            component={FormInput}
                                            validator={PhoneValidatorNotRequired}
                                        />
                                        <Field
                                            label='Tertiary Phone:'
                                            name='TertiaryPhone'
                                            id='TertiaryPhone'
                                            component={FormInput}
                                            validator={PhoneValidatorNotRequired}
                                        />
                                    </Col>
                                </Row>
                                <Row className='mt-3 justify-content-end'>
                                    <div className='d-flex justify-content-end'>
                                        <Button className='me-1' variant='outline-secondary btn-lg' onClick={() => setShowDealerContactModal(false)}>Cancel</Button>
                                        <SaveButton type='submit' disabled={!formRenderProps.allowSubmit} />
                                    </div>
                                </Row>
                            </FormElement>
                        )
                        }
                    />
                </Modal.Body>
            </Modal>

        </>
    )
}
export default CaseInformationForm

